import { platformApi, getPlatformApiErrorMessage } from './platformApi';

/**
 * Activity Logs client.
 *
 * The label, category and tone on every record are resolved SERVER-SIDE and travel with it, so
 * this layer never maps an action string to display text. That is deliberate: the old console
 * carried two divergent label maps (one on the Security page, a regex on the Audit Log page)
 * and both rendered raw tokens for anything they missed. A component here cannot render a raw
 * token because it never receives one.
 */

export const ACTIVITY_CATEGORIES = [
  'authentication', 'access', 'people', 'tenants', 'matters', 'documents',
  'communication', 'money', 'conflicts', 'billing', 'support', 'system',
] as const;

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

export const ACTIVITY_CATEGORY_LABEL: Record<ActivityCategory, string> = {
  authentication: 'Sign-in & security',
  access:         'Permissions & access',
  people:         'People & accounts',
  tenants:        'Tenants & workspaces',
  matters:        'Matters & records',
  documents:      'Documents',
  communication:  'Email & communication',
  money:          'Fees & payments',
  conflicts:      'Conflicts',
  billing:        'Billing & plans',
  support:        'Support & configuration',
  system:         'System',
};

export const ACTIVITY_SOURCES = ['tenant', 'platform', 'permission'] as const;
export type ActivitySource = (typeof ACTIVITY_SOURCES)[number];

export const ACTIVITY_SOURCE_LABEL: Record<ActivitySource, string> = {
  tenant:     'Chambers activity',
  platform:   'Platform console',
  permission: 'Permission changes',
};

export type ActivityTone = 'created' | 'changed' | 'removed' | 'viewed' | 'money' | 'auth' | 'alert';
/** Authoritative — resolved server-side from users.member_type, never from an email. */
export const ACTOR_KINDS = [
  'platform_admin', 'chamber_admin', 'barrister', 'clerk',
  'solicitor', 'chambers_staff', 'system',
] as const;

export type ActorKind = (typeof ACTOR_KINDS)[number];

export const ACTOR_KIND_LABEL: Record<ActorKind, string> = {
  platform_admin: 'Platform admin',
  chamber_admin:  'Chamber admin',
  barrister:      'Barrister',
  clerk:          'Clerk',
  solicitor:      'Solicitor',
  chambers_staff: 'Chambers staff',
  system:         'System',
};

export interface ActivityRecord {
  id: string;
  source: ActivitySource;
  occurredAt: string;
  sortKey: string;
  category: ActivityCategory;
  /** Raw ledger value — used for filtering, never rendered. */
  action: string;
  label: string;
  tone: ActivityTone;
  actor: { id: string | null; email: string | null; displayName: string | null; kind: ActorKind };
  organisation: { id: string; name: string; slug: string | null } | null;
  target: { type: string | null; id: string | null; label: string | null; redacted: boolean } | null;
  summary: string | null;
  detail: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  integrity: { chained: boolean; sequenceNumber: string | null };
}

export interface ActivityPage {
  records: ActivityRecord[];
  nextCursor: string | null;
  hasMore: boolean;
  total: number;
  countMode: 'exact' | 'atLeast';
  window: { from: string; to: string; clamped: boolean };
}

export interface ActivitySummary {
  successfulLogins24h: number;
  failedLogins24h: number;
  failureRate: number;
  sessions: { chambers: number; platform: number };
}

export interface ActivityFilters {
  from?: string;
  to?: string;
  source?: ActivitySource;
  category?: ActivityCategory;
  action?: string;
  actor?: string;
  /** WHO acted, by authoritative role. */
  actorType?: ActorKind;
  organisationId?: string;
  targetId?: string;
}

export async function fetchActivity(
  filters: ActivityFilters,
  opts: { cursor?: string | null; limit?: number } = {},
): Promise<ActivityPage> {
  const res = await platformApi.get('/activity', {
    params: { ...filters, ...(opts.cursor ? { cursor: opts.cursor } : {}), limit: opts.limit ?? 50 },
  });
  return res.data?.data ?? res.data;
}

export async function fetchActivitySummary(): Promise<ActivitySummary> {
  const res = await platformApi.get('/activity/summary');
  return res.data?.data ?? res.data;
}

/**
 * Download the current filter as CSV.
 *
 * Fetched as a blob rather than navigated to, because the endpoint needs the Authorization
 * header a plain <a href> cannot carry. The server refuses (400) rather than truncating when
 * the filter matches more than the export ceiling — that message is surfaced verbatim.
 */
export async function downloadActivityCsv(filters: ActivityFilters): Promise<number> {
  try {
    const res = await platformApi.get('/activity/export', { params: filters, responseType: 'blob' });

    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);

    return Number(res.headers['x-row-count'] ?? 0);
  } catch (err) {
    // A blob responseType means an error body arrives as a Blob too, so the server's
    // "narrow the filter" message would otherwise be lost behind a generic failure.
    const blob = (err as { response?: { data?: unknown } })?.response?.data;
    if (blob instanceof Blob) {
      try {
        const parsed = JSON.parse(await blob.text()) as { error?: { message?: string } };
        if (parsed.error?.message) throw new Error(parsed.error.message);
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message) throw parseErr;
      }
    }
    throw new Error(getPlatformApiErrorMessage(err));
  }
}
